import { useCallback, useEffect, useRef } from "react";
import { Box, BoxProps, Flex } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useNewsTrendingQuery } from "src/api/client/news-trending.api";
import { ArrowLink } from "src/components/base/arrow-link";
import { WfHeading } from "src/components/base/wf-heading";
import { EmptyState } from "src/components/common/empty-state";
import { FeedCardNewsSkeleton } from "src/components/feed-card/skeleton/feed-card-news-skeleton";
import { EnumPageType, initializeFeedTrackingStore } from "src/components/feed-card/store/feed-tracking-store";
import { FeedCardList } from "src/components/feed-card-list/feed-card-list";
import { useFeedItemSeenTracking } from "src/components/pages/feed/hooks/use-feed-item-seen-tracking";
import useViewportSpy from "src/hooks/dom/use-viewport-spy";
import { ILink } from "src/types/common";
import { ContentSeenItem } from "../feed/types/types";

const LoadingPlaceholder = () => (
    <Flex gap={3} flexFlow="column">
        <FeedCardNewsSkeleton />
        <FeedCardNewsSkeleton />
        <FeedCardNewsSkeleton />
    </Flex>
);

interface ITrendingNewsArticlesSection extends BoxProps {
    moreNewsFeedLink: ILink;
    slug: string;
}

export const TrendingNewsArticlesSection = ({ moreNewsFeedLink, slug, ...boxProps }: ITrendingNewsArticlesSection) => {
    const { t } = useTranslation(["common", "news"]);
    const sectionRef = useRef<HTMLDivElement>(null);
    const isSectionVisible = useViewportSpy(sectionRef, { threshold: 0.1, visibleOnce: true });
    const { data, isLoading, isError } = useNewsTrendingQuery({ count: 3, currentNewsSlug: slug, enabled: isSectionVisible });
    const seenItemsRef = useRef<Map<string, ContentSeenItem>>(new Map());
    const { trackSeenItem } = useFeedItemSeenTracking(seenItemsRef);

    useEffect(() => initializeFeedTrackingStore({ page: EnumPageType.ExternalNews }), []);

    const onFirstSeen = useCallback(
        (contentId: string) => {
            trackSeenItem(contentId);
        },
        [trackSeenItem]
    );

    return (
        <Box as="section" {...boxProps} ref={sectionRef}>
            <WfHeading as="h2" fontSize="2xl" mb="2">
                {t("headline.news", { ns: "news" })}
            </WfHeading>

            {(isLoading || data === undefined) && <LoadingPlaceholder />}
            {!data?.data.results && isError && <EmptyState title={t("generic-error-heading")} description={t("generic-error-message")} />}
            {data?.data.results ? <FeedCardList feedCards={data.data.results} onItemFirstSeen={onFirstSeen} /> : null}
            {data?.data.results.length === 0 ? <EmptyState title={t("headline.no-news", { ns: "news" })} /> : null}
            <Flex pt={6} width="100%" justifyContent={["end", "left"]}>
                <ArrowLink display="inline-block" href={moreNewsFeedLink.url} target={moreNewsFeedLink.target}>
                    {moreNewsFeedLink.name}
                </ArrowLink>
            </Flex>
        </Box>
    );
};
